// Voice preference storage shared by TTS providers.
//
// Server-backed providers store `{provider, id, rate}` in
// `user.ttsVoices[language][feature]`. Local providers own their localStorage
// keys because legacy browser keys are provider-specific.

import readlangClient from "readlangClient";
import * as ttsPolicy from "shared/ttsPolicy";

import type { TTSFeature } from "./index";
import type { TTSProvider, TTSTier, TTSVoice } from "./internalTypes";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type UserModelLike = any;

interface StoredVoicePreference {
  id: string;
  rate?: number;
}

interface ResolvedStoredPreference {
  provider: TTSProvider;
  voice: TTSVoice;
  rate?: number;
}

export const tierFromUser = (user: UserModelLike): TTSTier =>
  ttsPolicy.tierFromSubscriptionPlan(user.state.peek().subscriptionPlan);

const meetsTier = (userTier: TTSTier, required: TTSTier): boolean =>
  ttsPolicy.tierMeetsRequirement(userTier, required);

const providerMeetsTier = (
  user: UserModelLike,
  provider: TTSProvider,
  feature: TTSFeature
): boolean => meetsTier(tierFromUser(user), provider.minTierByFeature[feature]);

export const canUseProvider = (
  user: UserModelLike,
  provider: TTSProvider,
  feature: TTSFeature,
  language: string
): boolean =>
  providerMeetsTier(user, provider, feature) &&
  provider.supportsLanguage(language, feature);

const otherFeature = (feature: TTSFeature): TTSFeature =>
  feature === "readAloud" ? "speakingMode" : "readAloud";

const getServerPreference = (
  user: UserModelLike,
  provider: TTSProvider,
  feature: TTSFeature,
  language: string
): StoredVoicePreference | undefined => {
  const entry = (user.state.peek().ttsVoices || {})[language];
  if (!entry) return undefined;

  // Legacy server prefs stored one shared `{provider, id}` directly under the
  // language. Keep reading that shape until the user picks per-feature.
  const perFeature = entry[feature];
  if (perFeature?.provider === provider.id && perFeature.id) {
    return {
      id: perFeature.id,
      ...(typeof perFeature.rate === "number" ? { rate: perFeature.rate } : {}),
    };
  }

  if (entry.provider === provider.id && entry.id) {
    return {
      id: entry.id,
      ...(typeof entry.rate === "number" ? { rate: entry.rate } : {}),
    };
  }
  return undefined;
};

const getStoredPreference = (
  user: UserModelLike,
  provider: TTSProvider,
  feature: TTSFeature,
  language: string
): StoredVoicePreference | undefined => {
  if (provider.storage === "server") {
    return getServerPreference(user, provider, feature, language);
  }
  const id = provider.getLocalVoiceId?.(feature, language);
  return id ? { id } : undefined;
};

const resolveStoredPreference = (
  user: UserModelLike,
  feature: TTSFeature,
  language: string,
  providers: TTSProvider[]
): ResolvedStoredPreference | undefined => {
  for (const provider of providers) {
    const preference = getStoredPreference(user, provider, feature, language);
    if (!preference || !providerMeetsTier(user, provider, feature)) continue;

    const voice = provider.getVoice(language, preference.id);
    if (voice) {
      return { provider, voice, rate: preference.rate };
    }
  }
  return undefined;
};

export const resolveVoice = (
  user: UserModelLike,
  feature: TTSFeature,
  language: string,
  providers: TTSProvider[]
): TTSVoice | undefined =>
  resolveStoredPreference(user, feature, language, providers)?.voice;

const firstLocalProvider = (
  providers: TTSProvider[]
): TTSProvider | undefined =>
  providers.find((provider) => provider.storage === "local");

export const getRate = (
  user: UserModelLike,
  feature: TTSFeature,
  language: string,
  providers: TTSProvider[]
): number => {
  const resolved = resolveStoredPreference(user, feature, language, providers);
  if (resolved?.provider.storage === "server") return resolved.rate ?? 1;

  const localProvider =
    resolved?.provider.storage === "local"
      ? resolved.provider
      : firstLocalProvider(providers);
  return localProvider?.getLocalRate?.(feature, language) ?? 1;
};

const writeServerPreference = async (
  user: UserModelLike,
  language: string,
  feature: TTSFeature,
  voice: { provider: string; id: string; rate?: number } | null
): Promise<void> => {
  const response = await readlangClient.request<{
    ttsVoices: Record<string, unknown>;
  }>("PATCH", "/api/user/ttsVoices", { language, feature, voice });
  user.patch({ ttsVoices: response.data.ttsVoices });
};

export const setVoice = async (
  user: UserModelLike,
  language: string,
  voice: TTSVoice,
  feature: TTSFeature,
  providers: TTSProvider[]
): Promise<void> => {
  const provider = providers.find(
    (candidate) => candidate.id === voice.provider
  );
  if (!provider) return;

  const currentRate = getRate(user, feature, language, providers);

  if (provider.storage === "server") {
    await writeServerPreference(user, language, feature, {
      provider: provider.id,
      id: voice.id,
      rate: currentRate,
    });
    return;
  }

  const mirroredFeature = otherFeature(feature);
  // Check before writing the current feature because local providers may keep
  // shared legacy fallback keys that would otherwise make the other feature
  // look populated after this write.
  const shouldMirror = !provider.getLocalVoiceId?.(mirroredFeature, language);

  provider.setLocalVoiceId?.(feature, language, voice.id);
  provider.setLocalRate?.(feature, language, currentRate);

  if (shouldMirror) {
    provider.setLocalVoiceId?.(mirroredFeature, language, voice.id);
    provider.setLocalRate?.(mirroredFeature, language, currentRate);
  }

  for (const serverProvider of providers.filter(
    (candidate) => candidate.storage === "server"
  )) {
    // Switching this feature to a local provider must clear any server-backed
    // preference for the same feature so explicit local picks win.
    if (getServerPreference(user, serverProvider, feature, language)) {
      await writeServerPreference(user, language, feature, null);
    }
  }
};

export const setRate = async (
  user: UserModelLike,
  feature: TTSFeature,
  language: string,
  rate: number,
  providers: TTSProvider[]
): Promise<void> => {
  const resolved = resolveStoredPreference(user, feature, language, providers);
  if (resolved?.provider.storage === "server") {
    await writeServerPreference(user, language, feature, {
      provider: resolved.provider.id,
      id: resolved.voice.id,
      rate,
    });
    return;
  }

  const localProvider =
    resolved?.provider.storage === "local"
      ? resolved.provider
      : firstLocalProvider(providers);
  localProvider?.setLocalRate?.(feature, language, rate);
};
